<template>
  <div class="relative flex flex-col w-full h-full dark:bg-dark-box-background">
    <div class="w-full flex">
      <div class="w-full flex items-center">
        <Database color="#B171F9" class="w-5 h-5 mr-2" />
        <span class="font-bold">{{ $t("Page Data Source") }}</span>
      </div>
      <button
        v-if="!dynamic_data_source_disabled && (show_select_record || show_select_group)"
        class="cancel-btn w-fit"
        @click="
          () => {
            show_select_record = false
            show_select_group = false
            selected_specific_record = null
            selected_group = null
          }
        ">
        <RotateCcw color="#000" class="w-5 h-5 mr-2" />
        <span>{{ $t("Reset") }}</span>
      </button>
    </div>
    <div v-if="dynamic_data_source_disabled" class="flex flex-col items-center justify-center">
      <Ban color="#899399" class="w-5 h-5 mr-2" />
      <span class="text-[#899399] text-sm text-center">{{
        $t("Data Source can't be selected because you have components with mixed object types in the page.")
      }}</span>
    </div>
    <div v-else>
      <div v-if="!show_select_record && !show_select_group" class="w-full flex mb-1">
        <span class="flex flex-col items-end text-[14px] not-italic font-normal font-sans leading-normal text-common-grey">{{
          $t("Select Data Source Object type")
        }}</span>
      </div>
      <div v-if="!show_select_record && !show_select_group" class="w-full flex gap-1 mt-auto">
        <ComboBoxInput
          v-model:current-item="selected_object_type"
          :disabled="currentTemplate.layout.length > 22"
          :value="selected_object_type"
          :items="$store.state.object_types"
          name="object_type"
          classes="w-full relative dark:text-white"
          label-attr="label"
          value-attr="type"
          :is-box-background="true"
          label=""
          :show-label="true"
          :floating-label="false"
          :allow-null="false" />
        <ComboBoxInput
          v-model:current-item="selected_data_source"
          :disabled="currentTemplate.layout.length > 22"
          :value="selected_data_source"
          :items="data_sources"
          name="data_source"
          classes="w-full relative dark:text-white"
          label-attr="name"
          value-attr="id"
          :is-box-background="true"
          label=""
          :show-label="true"
          :floating-label="false"
          :allow-null="false" />
        <button
          class="primary-btn w-fit"
          @click="selected_data_source == 'specific_record' ? (show_select_record = true) : (show_select_group = true)">
          <Plus class="w-5 h-5 mr-1 text-white" />
          <span>{{ $t("Set") }}</span>
        </button>
      </div>
      <div v-else-if="show_select_record" class="w-full flex gap-1 mt-auto">
        <AsyncMultiSelectCompaniesInput
          :multi-select="false"
          :object-type="selected_object_type"
          :add-label="$utils.capitalize($utils.makePlural(selected_object_type))"
          :clear-on-select="true"
          :is-box-background="true"
          :value="selected_specific_record ? [selected_specific_record] : []"
          :initial-value="selected_specific_record ? [selected_specific_record] : []"
          classes="w-full"
          input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm "
          name="companies"
          value-attr="id"
          :endpoint="`/api/organizations/${$store.state.organization.id}/records/filter/`"
          endpoint-method="post"
          search-field="name"
          type="text"
          :placeholder="$utils.capitalize($utils.makePlural(selected_object_type))"
          @update:currentItems="
            ($event) => {
              handleChangeSelectedRecord($event)
            }
          " />
      </div>
      <div v-else-if="show_select_group" class="w-full flex gap-1 mt-auto">
        <SelectGroupDropDownMenu
          v-model:current-item="selected_group"
          :clear-on-select="true"
          :items="
            $store.state.data?.groups.filter(
              (group) => group.type == selected_object_type && group.organization == $store.state.organization.id
            )
          "
          :object-type="selected_object_type"
          :clear-selected-item="!selected_group ? true : false"
          label-attr="name"
          name="groups"
          :placeholder="$t('Groups')"
          class="w-full relative"
          :is-box-background="true"
          :input-classes="`'w-full sm:text-sm appearance-none h-9 font-medium text-gray-700 text-left block px-1 py-2 rounded-lg shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light`"
          @groupSelection="handleGroupSelected()" />
      </div>
    </div>
  </div>
</template>

<script>
import AsyncMultiSelectCompaniesInput from "@/components/shared/AsyncMultiSelectCompaniesInput.vue"
import ComboBoxInput from "@/components/shared/ComboBoxInput.vue"
import SelectGroupDropDownMenu from "@/components/shared/SelectGroupDropDownMenu.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { Ban, Plus, Database, RotateCcw } from "lucide-vue-next"

export default {
  components: { RotateCcw, Ban, Plus, Database, AsyncMultiSelectCompaniesInput, ComboBoxInput, SelectGroupDropDownMenu },
  inject: ["setPageLayout"],
  props: {
    currentTemplate: {
      type: Object,
      required: true
    },
    pageReference: {
      type: String,
      required: true
    }
  },
  emits: ["update:currentTemplate"],

  data() {
    return {
      selected_object_type: "company",
      selected_data_source: "specific_record",
      data_sources: [
        { name: this.$t("Specific Record"), id: "specific_record" },
        { name: this.$t("Group"), id: "group" }
      ],
      selected_specific_record: null,
      selected_group: null,
      show_select_record: false,
      show_select_group: false,
      prevent_select_record: false,
      prevent_select_group: false,
      prevent_watch_trigger: false
    }
  },

  computed: {
    dynamic_data_source_disabled() {
      if (this.pageReference != "dashboard") {
        return true
      }
      this.currentTemplate?.layout.map((layout) => {
        if (
          layout.props.component_type == "record_title" ||
          layout.props.component_type == "assets" ||
          layout.props.component_type == "checklist" ||
          layout.props.component_type == "company_map" ||
          layout.props.component_type == "overview" ||
          layout.props.component_type == "owners" ||
          layout.props.component_type == "key_people" ||
          layout.props.component_type == "last_touch" ||
          layout.props.component_type == "last_quality_touch" ||
          layout.props.component_type == "notes"
        ) {
          this.prevent_select_group = true
          this.data_sources = [{ name: this.$t("Specific Record"), id: "specific_record" }]
        }
        if (layout.props.component_type == "forecast_table" || layout.props.component_type == "journey") {
          this.prevent_select_record = true
          this.data_sources = [{ name: this.$t("Group"), id: "group" }]
        }
      })
      if (this.prevent_select_group && this.prevent_select_record) {
        return true
      }
      return false
    }
  },

  watch: {
    prevent_select_group() {
      if (!this.prevent_select_group && !this.prevent_select_record) {
        this.data_sources = [
          { name: this.$t("Specific Record"), id: "specific_record" },
          { name: this.$t("Group"), id: "group" }
        ]
      }
    },
    prevent_select_record() {
      if (!this.prevent_select_group && !this.prevent_select_record) {
        this.data_sources = [
          { name: this.$t("Specific Record"), id: "specific_record" },
          { name: this.$t("Group"), id: "group" }
        ]
      }
    },
    currentTemplate() {
      this.initSelections()
    },
    selected_group() {
      if (this.selected_group) {
        this.selected_specific_record = null
      }
      if (!this.prevent_watch_trigger) {
        this.setDynamicDataSourceLayoutComponent()
      }
    },
    selected_specific_record() {
      if (this.selected_specific_record) {
        this.selected_group = null
      }
      if (!this.prevent_watch_trigger) {
        this.setDynamicDataSourceLayoutComponent()
      }
    },
    selected_object_type() {
      this.selected_group = null
      this.selected_specific_record = null

      if (!this.prevent_watch_trigger) {
        this.setDynamicDataSourceLayoutComponent()
      }
    }
  },

  created() {
    this.initSelections()
  },

  methods: {
    initSelections() {
      this.prevent_watch_trigger = true
      setTimeout(() => {
        this.prevent_select_record = false
        this.prevent_select_group = false
        this.prevent_watch_trigger = false
      }, 2000)
      const settings_component_in_layout = this.currentTemplate?.layout.find((l) => l.id == "dynamic_source_selection")
      if (settings_component_in_layout) {
        const data_source_array = settings_component_in_layout.data_source_string.split(">")
        this.selected_object_type = data_source_array[0]
        if (data_source_array[1] == "group") {
          this.selected_group = this.$store.state.data?.groups.find((g) => g.id == data_source_array[2])
          this.show_select_group = true
        } else if (data_source_array[1] == "specific_record") {
          this.$store
            .dispatch("data/getRecord", data_source_array[2])
            .then((res) => {
              this.selected_specific_record = res.data
              this.show_select_record = true
            })
            .catch(() => {
              setTimeout(() => {
                this.$store.dispatch("data/getRecord", data_source_array[2]).then((res) => {
                  this.selected_specific_record = res.data
                  this.show_select_record = true
                })
              }, 1000)
            })
        }
      }
    },
    handleChangeSelectedRecord(e) {
      this.selected_specific_record = e[0]
      setTimeout(() => {
        if (this.currentTemplate.layout.length < 2) {
          this.$emitter.emit(ALL_EMITTERS.ADD_NEW_COMPONENT, { page_reference: this.pageReference, current_template: this.currentTemplate })
        }
      }, 800)
    },
    handleGroupSelected() {
      setTimeout(() => {
        if (this.currentTemplate.layout.length < 2) {
          this.$emitter.emit(ALL_EMITTERS.ADD_NEW_COMPONENT, { page_reference: this.pageReference, current_template: this.currentTemplate })
        }
      }, 800)
    },
    setDynamicDataSourceLayoutComponent() {
      if (!this.selected_group && !this.selected_specific_record) {
        this.removeDynamicDataSourceLayoutComponent()
        return
      }

      let data_source_string = ""
      if (this.selected_group) {
        data_source_string = `${this.selected_object_type}>group>${this.selected_group.id}`
      } else if (this.selected_specific_record) {
        data_source_string = `${this.selected_object_type}>specific_record>${this.selected_specific_record.id}`
      }

      const current_template_clone = this.$utils.clone(this.currentTemplate)
      const settings_component_obj = {
        props: { hidden: true, component_type: "dynamic_source_selection" },
        id: "dynamic_source_selection",
        data_source_string: data_source_string
      }

      this.$utils.replaceOrAddItem(current_template_clone.layout, settings_component_obj)

      current_template_clone.layout.forEach((component) => {
        if (component.id !== "dynamic_source_selection") {
          const component_clone = this.$utils.clone(component)
          if (this.selected_group) {
            if (component_clone.props.component_type == "rollup" || component_clone.props.component_type == "score") {
              component_clone.props.component_props.dataSource = this.selected_group.id
              component_clone.props.component_props.linkedRecord = null
              component_clone.props.component_props.objectType = this.selected_object_type
            } else if (component_clone.props.component_type == "graph") {
              component_clone.props.component_props.dataSource = this.selected_group.id
            } else {
              component_clone.props.component_props.view_by = this.selected_group.id
            }
          } else if (this.selected_specific_record) {
            if (component_clone.props.component_type == "assets") {
              component_clone.props.component_props.connectTo = "specific_record"
              component_clone.props.component_props.linked_to = "specific_record"
              component_clone.props.component_props.recordId = this.selected_specific_record.id
            } else if (component_clone.props.component_type == "notes") {
              component_clone.props.component_props.connectTo = "this_page"
              component_clone.props.component_props.linked_to = "specific_record"
              component_clone.props.component_props.recordId = this.selected_specific_record.id
            } else if (component_clone.props.component_type == "record_table") {
              component_clone.props.component_props.dataSource = "another_record"
              component_clone.props.component_props.linkedRecord = this.selected_specific_record.id
              component_clone.props.component_props.objectType = this.selected_object_type
              component_clone.props.component_props.preservedMainFilter = ""
              component_clone.props.component_props.preservedColumnsFilters = ""
            } else if (component_clone.props.component_props.initialFilter || component_clone.props.component_type == "action_table") {
              if (this.selected_object_type == "company") {
                component_clone.props.component_props["initialFilter"] = {
                  contacts: [],
                  companies: [this.selected_specific_record]
                }
              } else if (this.selected_object_type == "contact") {
                component_clone.props.component_props["initialFilter"] = {
                  companies: [],
                  contacts: [this.selected_specific_record]
                }
              }
            } else if (component_clone.props.component_type == "rollup" || component_clone.props.component_type == "score") {
              component_clone.props.component_props.dataSource = "another_record"
              component_clone.props.component_props.linkedRecord = this.selected_specific_record.id
              component_clone.props.component_props.objectType = this.selected_object_type
            } else if (component_clone.props.component_type == "graph") {
              component_clone.props.component_props.dataSource = this.selected_specific_record.id
            } else if (component_clone.props.component_props.linkedRecord) {
              component_clone.props.component_props.linkedRecord = this.selected_specific_record.id
            }
          }
          this.$utils.replaceOrAddItem(current_template_clone.layout, component_clone)
        }
      })
      this.setPageLayout(current_template_clone)
      this.$emit("update:currentTemplate", current_template_clone)
      this.$emitter.emit(ALL_EMITTERS.LAYOUT_UPDATED, { layout: current_template_clone, silent: true })
    },

    removeDynamicDataSourceLayoutComponent() {
      const current_template_clone = this.$utils.clone(this.currentTemplate)
      this.$utils.removeItem(current_template_clone.layout, { id: "dynamic_source_selection" })

      this.setPageLayout(current_template_clone)
      this.$emit("update:currentTemplate", current_template_clone)
      this.$emitter.emit(ALL_EMITTERS.LAYOUT_UPDATED, { layout: current_template_clone, silent: true })
    }
  }
}
</script>
